/**
 * Going very fast, seen from the cockpit.
 *
 * Stars smearing past says *you are moving* in a way a speed readout cannot. It
 * winds in above `STREAK_SPEED` whatever put you there — a warp run across a
 * system is as fast as a jump — so this is not a crossing effect and does not
 * wait for one.
 *
 * ── Why this is in the scene rather than on a canvas over it ────────────────
 *
 * It was a 2D canvas layered above the render, which is the cheapest way to draw
 * radial streaks and has one fatal property: a canvas has no depth, so it paints
 * over everything including your own hull. Stars you are passing appeared *in
 * front of* the ship passing them, which reads as a smeared windscreen rather
 * than as speed.
 *
 * No stacking order fixes that — the ship is inside the WebGL canvas and the
 * streaks were outside it — so the streaks had to come in. They are drawn as
 * ordinary depth-tested geometry, and the hull, being opaque and nearer, simply
 * occludes them. So does a planet you tear past.
 *
 * ── The look is deliberately the old one ────────────────────────────────────
 *
 * The simulation below is the canvas version's, unchanged: an angle, a radius as
 * a fraction of the screen's half-diagonal, and an outward speed that accelerates
 * with distance. That is what perspective does to something you fly past — slow
 * while it is ahead of you, quick as it goes by the canopy — and it was already
 * tuned. Only the drawing moved.
 *
 * ── Screen coordinates, placed at a depth ───────────────────────────────────
 *
 * Each streak is still positioned in *screen* terms and then put at a fixed
 * distance in front of the camera, scaled so it covers the same part of the view
 * it always did. The distance is the entire trick: far enough that the ship is
 * always nearer, so depth resolves the way round the effect needs, and near
 * enough that a body you are actually flying past can cut across it.
 */

import * as THREE from 'three';

/**
 * Speed at which the stars begin to smear, in world units per second.
 *
 * Well above anything the main drive can do — cruise is 120 — so this is a
 * warp-only sensation. Full strength at `WARP_SPEED`, which is where a jump's
 * run-up ends, so a crossing arrives with the streaks already up.
 */
const STREAK_SPEED = 600;
const STREAK_FULL = 800;

/** Seconds for the crossing's own contribution to wind in, and to let go. */
const CROSSING_IN = 2.2;

/**
 * Faster out than in, and deliberately so.
 *
 * Arriving, the ship is easing in from the edge of the system with the star
 * ahead of it — the part worth watching. Winding down at the same rate it wound
 * up meant watching it through a haze that had no reason to still be there.
 */
const CROSSING_OUT = 1.0;

/** How many streaks at warp. Doubled while crossing. */
const STREAKS = 320;

/**
 * How far from the middle a streak starts.
 *
 * Not zero: a star at the exact centre has no direction to fly away along, and a
 * cluster of them there reads as a smudge rather than as the point you are
 * heading towards.
 */
const INNER = 0.02;

/**
 * How far out a streak has to get before it is at full brightness, as a
 * fraction of the half-diagonal.
 *
 * A streak is born near the middle and has to arrive rather than pop, so it
 * fades up as it leaves. The distance over which it does that was `1/3` — a
 * third of the way to the corner — which put a dark disc over the middle of the
 * screen with every streak crossing into visibility at the same radius. That
 * common threshold is a *ring*, and the denser the field the more plainly it
 * reads as one, which is why a crossing showed it worst.
 *
 * Short enough now that a streak is up to strength almost as soon as it exists,
 * so the middle of the screen is the busiest part of it — which is what flying
 * towards something looks like.
 */
const FADE_IN = 0.07;

/**
 * How much the birth radius varies, as a multiple of `INNER`.
 *
 * Every streak used to be reborn at exactly `INNER`, so they all crossed the
 * fade at the same distance and the ring had a hard edge. Spreading the start
 * smears that crossing over a band instead of a circle.
 */
const INNER_SPREAD = 2;

/**
 * How far in front of the camera the streaks are hung, in world units.
 *
 * **This is the constant that decides what can occlude them.** Everything nearer
 * draws in front; everything further is drawn over. It has to clear the ship by
 * a wide margin — the chase camera already sits several hull depths back, and a
 * massive hull is ninety units across — without being so far out that the effect
 * stops interacting with the system at all.
 *
 * Well inside `CROSSING_SHELL` too, so the crossing shell still erases them
 * along with everything else it is hiding.
 */
const DEPTH = 600;

export interface WarpStreaks {
  /**
   * Advance and redraw.
   *
   * `speed` is the ship's, and `crossing` says whether it is between systems —
   * which holds the effect up regardless of speed and doubles how dense it is.
   */
  step: (seconds: number, camera: THREE.PerspectiveCamera, speed: number, crossing: boolean) => void;
  dispose: () => void;
}

interface Streak {
  angle: number;
  /** Distance from centre, as a fraction of the screen's half-diagonal. */
  radius: number;
  speed: number;
  /** 0..1, dimmer ones sit behind. */
  bright: number;
}

function seedStreak(): Streak {
  return {
    angle: Math.random() * Math.PI * 2,
    // Square-rooted so they spread evenly over the area rather than bunching at
    // the middle, which is what a uniform radius would do.
    radius: INNER + Math.sqrt(Math.random()) * 0.6,
    speed: 0.35 + Math.random() * 0.85,
    bright: 0.35 + Math.random() * 0.65,
  };
}

/**
 * The field itself, which outlives the scene drawing it.
 *
 * ── Why this is module state rather than per-scene ──────────────────────────
 *
 * A jump *destroys the flight scene* and builds a new one for the system being
 * arrived at, and this effect lives in that scene. Seeded per scene, every
 * streak was therefore thrown away and re-rolled at the swap — so the sky
 * visibly snapped to a new arrangement, and the wind-up started again from
 * nothing, in the middle of the one manoeuvre the effect exists to sell.
 *
 * The gas solves the same problem by owning a renderer that outlives the swap.
 * That is not available here — the whole point of these being in the scene is
 * that the hull occludes them — so the *simulation* is what persists instead,
 * and only the geometry is rebuilt. There is one flight view and one sky, so a
 * single field at module scope is what that actually means.
 *
 * `intensity` and `dense` persist for the same reason and matter more than the
 * positions: they are the wind-up, and restarting those is what read as the
 * effect cutting out and coming back.
 *
 * Allocated for the crossing's doubled density; only the first half is drawn the
 * rest of the time, so an ordinary warp costs half as much.
 */
const streaks = Array.from({ length: STREAKS * 2 }, seedStreak);

/** Eased, so the effect does not step with the gauges. Survives a swap. */
let intensity = 0;
/** How far into a crossing, on its own clock rather than on speed. */
let dense = 0;

export function createWarpStreaks(scene: THREE.Scene): WarpStreaks {

  const positions = new Float32Array(STREAKS * 2 * 6);
  const colors = new Float32Array(STREAKS * 2 * 6);

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

  /*
   * Additive, unlit, and depth-*tested*.
   *
   * Tested but not written: a streak is light rather than a surface, so it must
   * not stop anything drawn after it, and two streaks crossing should add rather
   * than fight over which is in front. The testing is the whole point of moving
   * this into the scene — it is what puts the ship in front of the stars it is
   * passing.
   */
  const material = new THREE.LineBasicMaterial({
    vertexColors: true,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });

  const lines = new THREE.LineSegments(geometry, material);
  // The vertices are rebuilt in world space every frame, so a bounding box
  // computed once describes nothing. Culling against it would blink the effect
  // out at exactly the moment it is at full strength.
  lines.frustumCulled = false;
  /*
   * Above the crossing shell at 8, and that is not a detail.
   *
   * The shell erases the framebuffer down to the gas layer during a crossing. It
   * spares the hull only because the hull is opaque and wrote depth — these write
   * none, so drawn before the shell they were erased along with the system, and
   * a jump showed gas and no stars at all. The gas is *underneath* the canvas, so
   * losing the streaks did not reveal them behind it; it left the cloud on its
   * own, looking layered over the top.
   *
   * A crossing is the one time the streaks are at their densest — `crossing`
   * doubles them — so being hidden then was backwards.
   */
  lines.renderOrder = 9;
  lines.visible = false;
  scene.add(lines);

  const right = new THREE.Vector3();
  const up = new THREE.Vector3();
  const forward = new THREE.Vector3();
  const head = new THREE.Vector3();
  const tail = new THREE.Vector3();

  return {
    step: (seconds, camera, speed, crossing) => {
      const wanted = crossing ? 1 : 0;
      const rate = wanted > dense ? 1 / CROSSING_IN : 1 / CROSSING_OUT;
      dense += Math.sign(wanted - dense) * Math.min(Math.abs(wanted - dense), rate * seconds);

      /*
       * Speed drives it, and the crossing holds it up.
       *
       * Eased rather than taken raw: speed arrives from the gauges ten times a
       * second, not sixty, so the unsmoothed figure steps six frames at a time
       * and reads as flicker rather than as acceleration.
       */
      const fromSpeed = Math.min(
        1,
        Math.max(0, (Math.abs(speed) - STREAK_SPEED) / (STREAK_FULL - STREAK_SPEED)),
      );
      intensity += (Math.max(fromSpeed, dense) - intensity) * Math.min(1, seconds * 6);

      if (intensity <= 0.002) {
        lines.visible = false;
        return;
      }
      lines.visible = true;

      /*
       * The camera's own axes, and how much of the world one screen covers at
       * `DEPTH`.
       *
       * `reach` is the half-diagonal in world units at that distance, which is
       * the same thing the canvas version measured in pixels — so a radius of 1
       * still lands exactly on the corner of the screen whatever the aspect or
       * the field of view.
       */
      camera.updateMatrixWorld();
      right.setFromMatrixColumn(camera.matrixWorld, 0);
      up.setFromMatrixColumn(camera.matrixWorld, 1);
      forward.setFromMatrixColumn(camera.matrixWorld, 2).negate();

      const halfHeight = Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2) * DEPTH;
      const halfWidth = halfHeight * camera.aspect;
      const reach = Math.hypot(halfWidth, halfHeight);

      const count = Math.round(STREAKS * (1 + dense));
      let vertex = 0;

      for (let i = 0; i < count; i++) {
        const streak = streaks[i]!;
        const was = streak.radius;

        // Accelerating outward with distance, which is what perspective does to
        // anything you fly past: slow while it is ahead of you, quick as it goes
        // by the canopy.
        streak.radius += streak.speed * seconds * (0.35 + streak.radius * 2.6) * intensity;

        if (streak.radius > 1.5) {
          Object.assign(streak, seedStreak(), {
            radius: INNER + Math.random() * INNER * INNER_SPREAD,
          });
          continue;
        }

        const cos = Math.cos(streak.angle);
        const sin = Math.sin(streak.angle);

        // Length is the distance covered this frame, so the streak *is* the
        // motion rather than a decoration sized to look like it.
        const behind = Math.max(was, streak.radius - (streak.radius - was) * 9);

        // Screen offsets, turned into a point in front of the camera.
        head
          .copy(camera.position)
          .addScaledVector(forward, DEPTH)
          .addScaledVector(right, cos * streak.radius * reach)
          .addScaledVector(up, sin * streak.radius * reach);

        tail
          .copy(camera.position)
          .addScaledVector(forward, DEPTH)
          .addScaledVector(right, cos * behind * reach)
          .addScaledVector(up, sin * behind * reach);

        positions[vertex * 3] = tail.x;
        positions[vertex * 3 + 1] = tail.y;
        positions[vertex * 3 + 2] = tail.z;
        positions[vertex * 3 + 3] = head.x;
        positions[vertex * 3 + 4] = head.y;
        positions[vertex * 3 + 5] = head.z;

        /*
         * Brightness carried per vertex rather than as opacity.
         *
         * One material draws every streak in a single call, so a streak cannot
         * have its own alpha — but additive blending makes a dim colour and a
         * faint one the same thing, so brightness folds into the colour and
         * costs nothing.
         */
        const lit = Math.min(
          1,
          streak.bright * intensity * Math.min(1, streak.radius / FADE_IN),
        );
        const r = lit;
        const g = lit * (0.745 + streak.bright * 0.235);
        const b = lit * (0.588 + streak.bright * 0.353);

        colors[vertex * 3] = r;
        colors[vertex * 3 + 1] = g;
        colors[vertex * 3 + 2] = b;
        colors[vertex * 3 + 3] = r;
        colors[vertex * 3 + 4] = g;
        colors[vertex * 3 + 5] = b;

        vertex += 2;
      }

      // Only the streaks that were rebuilt this frame. A recycled one contributes
      // no vertices, so the count moves about slightly and the rest of the buffer
      // is simply not drawn rather than left showing last frame's sky.
      geometry.setDrawRange(0, vertex);
      geometry.attributes['position']!.needsUpdate = true;
      geometry.attributes['color']!.needsUpdate = true;
    },

    dispose: () => {
      scene.remove(lines);
      geometry.dispose();
      material.dispose();
    },
  };
}
